import React, { useEffect, useMemo, useState } from 'react';
import { apiCall } from '../lib/api';

/**
 * Repeat alert cadence editor used on both Partner and Tenant edit screens.
 *
 * Inheritance model (resolved server-side in nova-poller/src/repeatCadence.ts):
 *   tenant value  →  partner default  →  built-in system default
 *
 * For Partners we render the partner default directly (placeholder = system
 * default). For Tenants we accept an optional `partnerDefaults` prop so empty
 * inputs can show the value the tenant would inherit from its partner if no
 * tenant override is set.
 */

type CadenceMap = Record<string, number>;

interface SystemDefaultsResponse {
  source: string;
  unit: 'seconds';
  defaults: Record<string, number>;
}

export interface RepeatCadenceValue {
  enabled: boolean | null;
  cadenceSeconds: CadenceMap;
}

interface Props {
  /**
   * Current value. For tenants, `enabled === null` means "inherit from
   * partner"; for partners use `true`/`false` only.
   */
  value: RepeatCadenceValue;
  onChange: (next: RepeatCadenceValue) => void;
  /**
   * If provided, the editor renders inheritance hints and a "Reset to inherit"
   * button on the master toggle (used on the tenant page).
   */
  partnerDefaults?: {
    enabled: boolean;
    cadenceSeconds: CadenceMap;
  };
  /**
   * Display label for the parent layer when `partnerDefaults` is set, e.g. the
   * partner name. Falls back to "partner default".
   */
  parentLabel?: string;
}

// Alert types whose seconds inputs make sense to expose. Mirrors the keys used
// by the poller's SYSTEM_DEFAULTS, but skips ones that are inherently
// non-repeating (e.g. SESSION_START, OK, transient lifecycle events).
const EDITABLE_ALERT_TYPES: Array<{ key: string; label: string; help?: string }> = [
  { key: 'LEAK_IMMINENT', label: 'Leak imminent', help: 'High-priority alert; keep relatively low.' },
  { key: 'STAGNATION', label: 'Stagnation' },
  { key: 'CHANGE_NEEDED', label: 'Change needed' },
  { key: 'INVALID_DIAPER', label: 'Invalid diaper' },
  { key: 'DIAPER_TOO_OLD', label: 'Diaper too old' },
  { key: 'HARDWARE_FAULT_CHANGE_DIAPER', label: 'Hardware fault (change diaper)' },
  { key: 'CLIP_LOW_BATTERY', label: 'Clip low battery', help: 'Low urgency — repeat sparingly (e.g. once per hour).' },
  { key: 'CLIP_LOST_CONTACT', label: 'Clip lost contact' },
  { key: 'CLIP_DETACHED', label: 'Clip detached' },
  { key: 'CLIP_SHORTED', label: 'Clip shorted' },
];

export const RepeatCadenceEditor: React.FC<Props> = ({ value, onChange, partnerDefaults, parentLabel }) => {
  const [systemDefaults, setSystemDefaults] = useState<CadenceMap | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const showInheritControls = !!partnerDefaults;
  const parentName = parentLabel || 'partner default';

  useEffect(() => {
    let cancelled = false;
    apiCall('/api/configuration/repeat-cadence-defaults')
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data: SystemDefaultsResponse = await resp.json();
        if (!cancelled) setSystemDefaults(data.defaults || {});
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || String(err));
      });
    return () => { cancelled = true; };
  }, []);

  const effectiveEnabled = useMemo(() => {
    if (value.enabled !== null && value.enabled !== undefined) return value.enabled;
    if (partnerDefaults) return partnerDefaults.enabled;
    return true;
  }, [value.enabled, partnerDefaults]);

  const inputValueFor = (key: string): string => {
    const v = value.cadenceSeconds[key];
    return v === undefined || v === null ? '' : String(v);
  };

  /**
   * Resolve what the gate will actually use for this key, mirroring the
   * server-side merge order: tenant ← partner ← system. Returns null when no
   * layer has a value (extremely unlikely once system defaults are loaded).
   */
  const resolveEffective = (key: string): { seconds: number; source: 'override' | 'partner' | 'system' } | null => {
    const own = value.cadenceSeconds[key];
    if (own != null) return { seconds: own, source: 'override' };
    if (partnerDefaults && partnerDefaults.cadenceSeconds[key] != null) {
      return { seconds: partnerDefaults.cadenceSeconds[key], source: 'partner' };
    }
    if (systemDefaults && systemDefaults[key] != null) {
      return { seconds: systemDefaults[key], source: 'system' };
    }
    return null;
  };

  const placeholderFor = (key: string): string => {
    if (partnerDefaults && partnerDefaults.cadenceSeconds[key] != null) {
      return `${partnerDefaults.cadenceSeconds[key]} (${parentName})`;
    }
    if (systemDefaults && systemDefaults[key] != null) {
      return `${systemDefaults[key]} (system default)`;
    }
    return '';
  };

  const effectiveLabel = (key: string): { text: string; tone: 'override' | 'inherit' } | null => {
    const eff = resolveEffective(key);
    if (!eff) return null;
    if (eff.seconds === 0) {
      return { text: `Effective: never repeat${eff.source !== 'override' ? ` (${eff.source === 'partner' ? parentName : 'system default'})` : ''}`, tone: eff.source === 'override' ? 'override' : 'inherit' };
    }
    if (eff.source === 'override') {
      return { text: `Effective: ${eff.seconds}s · override`, tone: 'override' };
    }
    if (eff.source === 'partner') {
      return { text: `Effective: ${eff.seconds}s · ${parentName}`, tone: 'inherit' };
    }
    return { text: `Effective: ${eff.seconds}s · system default`, tone: 'inherit' };
  };

  const updateCadence = (key: string, raw: string) => {
    const next = { ...value.cadenceSeconds };
    if (raw.trim() === '') {
      delete next[key];
    } else {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) return;
      next[key] = Math.floor(n);
    }
    onChange({ ...value, cadenceSeconds: next });
  };

  return (
    <div className="space-y-4">
      {loadError && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Could not load system default cadence ({loadError}). Inputs still work; placeholders may be missing.
        </div>
      )}

      {/* Master toggle */}
      <div className="flex items-start gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={effectiveEnabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
          Repeat alerts enabled
        </label>
        {showInheritControls && value.enabled !== null && value.enabled !== undefined && (
          <button
            type="button"
            onClick={() => onChange({ ...value, enabled: null })}
            className="text-xs text-blue-600 hover:underline"
            title={`Inherit from ${parentName} (currently ${partnerDefaults?.enabled ? 'on' : 'off'})`}
          >
            Reset to inherit ({partnerDefaults?.enabled ? 'on' : 'off'})
          </button>
        )}
        {showInheritControls && (value.enabled === null || value.enabled === undefined) && (
          <span className="text-xs text-gray-400 italic">
            Inheriting from {parentName} ({partnerDefaults?.enabled ? 'on' : 'off'})
          </span>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Cadence is in seconds between repeat firings of the same alert type for the same client. Leave blank to
        {showInheritControls ? ` inherit from ${parentName} (or the system default).` : ' use the system default.'}
        {' '}A value of <code className="font-mono">0</code> disables repeats for that alert type.
      </p>

      {/* Per-alert-type cadence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {EDITABLE_ALERT_TYPES.map(({ key, label, help }) => (
          <div key={key} className="flex items-start gap-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="w-32 border rounded-lg px-3 py-1.5 text-sm"
                  value={inputValueFor(key)}
                  placeholder={placeholderFor(key)}
                  onChange={(e) => updateCadence(key, e.target.value)}
                  disabled={!effectiveEnabled}
                />
                <span className="text-xs text-gray-400">sec</span>
                {showInheritControls && value.cadenceSeconds[key] != null && (
                  <button
                    type="button"
                    onClick={() => updateCadence(key, '')}
                    className="text-xs text-blue-600 hover:underline"
                    title="Clear override and inherit"
                  >
                    inherit
                  </button>
                )}
              </div>
              {(() => {
                const eff = effectiveLabel(key);
                if (!eff) return null;
                const cls = eff.tone === 'override'
                  ? 'text-[11px] text-emerald-700 mt-0.5'
                  : 'text-[11px] text-gray-500 mt-0.5 italic';
                return <p className={cls}>{eff.text}</p>;
              })()}
              {help && <p className="text-[11px] text-gray-400 mt-0.5">{help}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
